import axios, {AxiosError, AxiosResponse} from 'axios';
import { API_URL } from '../config/api.config';

// ===== VARIABLES GLOBALES POUR GÉRER LE REFRESH =====
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const getAuthInfo = () => {
  const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || ''
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');

  console.log('🔍 getAuthInfo:', {
    garageId: !!garageId,
    token: !!token,
    refreshToken: !!refreshToken
  });

  return { garageId, token, refreshToken };
};

const updateStoredTokens = (newToken: string, newRefreshToken?: string) => {
  localStorage.setItem('token', newToken);
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
  }
  console.log('✅ Tokens mis à jour automatiquement');
};

const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.clear();
  console.log('🧹 Données d\'authentification supprimées');
};

const createAuthAxios = () => {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: 10000,
  });

  // Intercepteur de requête
  instance.interceptors.request.use(
      (config) => {
        const { token } = getAuthInfo();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
  );

  // 🎯 INTERCEPTEUR DE RÉPONSE AVEC PROTECTION CONTRE LES APPELS MULTIPLES
  instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config;
        console.log("🔍 Intercepteur déclenché:", {
          url: originalRequest?.url,
          status: error.response?.status,
          isRefreshing,
          queueLength: failedQueue.length
        });

        if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
          const errorData = error.response.data as any;

          // 🔄 CAS 1: Token expiré, refresh nécessaire
          if (errorData?.error === 'tokenExpired' && errorData?.needsRefresh) {
            console.log('🔄 Token expiré détecté');

            // ⭐ SI UN REFRESH EST DÉJÀ EN COURS
            if (isRefreshing) {
              console.log('⏳ Refresh déjà en cours, mise en queue de la requête...');

              // Mettre la requête en queue et attendre le résultat du refresh
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              }).then((token) => {
                if (originalRequest && originalRequest.headers) {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  console.log('🔄 Retry de la requête mise en queue:', originalRequest.url);
                  return instance(originalRequest);
                }
                return Promise.reject(new Error('Configuration de requête manquante'));
              }).catch(err => {
                return Promise.reject(err);
              });
            }

            // ⭐ MARQUER QUE LE REFRESH EST EN COURS
            (originalRequest as any)._retry = true;
            isRefreshing = true;

            console.log('🚀 Début du processus de refresh...');

            try {
              const { refreshToken: storedRefreshToken } = getAuthInfo();
              const refreshEndpoint = errorData.refreshEndpoint;

              if (!storedRefreshToken) {
                const error = new Error('Aucun refresh token disponible');
                processQueue(error, null);
                isRefreshing = false;
                throw error;
              }

              console.log(`📞 Appel refresh: ${API_URL}${refreshEndpoint}`);

              // Appel au bon endpoint selon le type d'utilisateur
              const refreshResponse = await axios.post(`${API_URL}${refreshEndpoint}`, {
                refreshToken: storedRefreshToken
              });

              if (refreshResponse.data.success && refreshResponse.data.token) {
                console.log('✅ Refresh réussi');

                // Mettre à jour les tokens
                updateStoredTokens(
                    refreshResponse.data.token,
                    refreshResponse.data.refreshToken
                );

                // ⭐ TRAITER TOUTES LES REQUÊTES EN QUEUE
                processQueue(null, refreshResponse.data.token);

                // Mettre à jour la requête originale avec le nouveau token
                if (originalRequest && originalRequest.headers) {
                  originalRequest.headers['Authorization'] = `Bearer ${refreshResponse.data.token}`;
                }

                // 🔄 RETRY de la requête originale
                console.log('🔄 Retry de la requête originale:', originalRequest?.url);
                return instance(originalRequest);
              } else {
                throw new Error('Échec du refresh: réponse invalide');
              }
            } catch (refreshError) {
              console.error('❌ Échec du refresh:', refreshError);

              // ⭐ REJETER TOUTES LES REQUÊTES EN QUEUE
              processQueue(refreshError, null);

              // Optionnel: nettoyer les données d'auth et rediriger
               clearAuthData();
               window.location.href = '/login';

              return Promise.reject(refreshError);
            } finally {
              // ⭐ IMPORTANT: Remettre À ZÉRO LE FLAG
              isRefreshing = false;
              console.log('🏁 Processus de refresh terminé');
            }
          }

          // 🔐 CAS 2: Authentification requise
          if (errorData?.needsAuth) {
            console.log('🔐 Authentification requise');
             clearAuthData();
             window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
  );

  return instance;
};

const authAxios = createAuthAxios();

export const inspectionService = {
  async getInspections() {
    try {
      const response = await axios.get(API_URL);
      return response.data;
    } catch (error) {
      console.error('Error fetching inspections:', error);
      throw error;
    }
  },
  async getAllInspectionsByGarageId() {
    try {
      const { garageId } = getAuthInfo();

      console.log("User data garageId: ", garageId);
      const response = await authAxios.get(`/inspections/garage/${garageId}`);
      console.log("Filtered inspections: ", response.data.inspections);
      return response.data.inspections;
    } catch (error) {
      console.error('Error get inspection by garage id:', error);
      throw error;
    }
  },
  async updateInspectionStatus(id: string, status: string) {
    try {
      const { garageId } = getAuthInfo();

      const response = await authAxios.put(`/inspections/${id}/status`, { status, garageId });
      console.log("update inspection status, ", status, garageId)
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  async getGarageStatsInspections(garageId: string) {
    try {
      const response = await authAxios.get(`/inspections/garage/${garageId}/stats`);

      console.log("getGarageStatsInspections : ", response.data)
      return response.data;
    } catch (error) {
      console.error('Error get inspection by garage id:', error);
      throw error;
    }
  },
  async getInspectionById(inspectionId: String, withFilter = true) {
    try {
      const { garageId } = getAuthInfo();
      if (withFilter) {
        const response =  await authAxios.get(`/inspections/garage/${garageId}/${inspectionId}`);
        return response.data;
      }
      else {
        const response =  await authAxios.get(`/inspections/${inspectionId}`);
        return response.data;
      }

    } catch (error) {
      console.error('Error get inspection by id:', error);
      throw error;
    }
  },
  async viewInspectionDetails(inspectionId: string) {
    try {
      const { garageId } = getAuthInfo();

      const response = await authAxios.post(`/inspections/${inspectionId}/view`, { garageId });

      console.log("viewInspectionDetails response:", response.data);

      return {
        inspection: response.data.inspection
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw {
          insufficientCredits: true,
          message: error.response.data.message || "Crédits insuffisants pour consulter cette inspection",
          creditsType: error.response.data.creditsType
        };
      }

      console.error('Error viewing inspection details:', error);
      throw error;
    }
  },


  async getUnreadMessageByInspectionId(inspectionId: string, garageId: string) {
    try {
      const response = await authAxios.get(`chat/messages/unread/${inspectionId}?userId=${garageId}`);
      return response.data.count;
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      throw error;
    }
  },
  async generateAIAnalysis(inspectionId: string) {
    try {
      const { garageId } = getAuthInfo();

      const response = await authAxios.post(`/inspections/${inspectionId}/ai-analysis`, { garageId });
      return response.data;
    } catch (error) {
      console.error('Error generating AI analysis:', error);
      throw error;
    }
  },
};